// Re-export types
export type {
  AudioBlockProps,
  GenericFileBlockProps,
  ImageBlockProps,
  MediaBlockProps,
  MediaBlockSize,
  PdfBlockProps,
  VideoBlockProps,
} from "@/components/ui/MediaBlocks/types";

// Re-export constants
export { MEDIA_BLOCK_SIZES, sizeConfig } from "@/components/ui/MediaBlocks/constants";

// Re-export components
export { AudioBlock } from "@/components/ui/MediaBlocks/AudioBlock";
export { GenericFileBlock } from "@/components/ui/MediaBlocks/GenericFileBlock";
export { ImageBlock } from "@/components/ui/MediaBlocks/ImageBlock";
export { OutputMediaBlock } from "@/components/ui/MediaBlocks/OutputMediaBlock";
export {
  MediaFullscreenDialog,
  type MediaFullscreenDialogProps,
  type MediaFullscreenDialogVariant,
} from "@/components/ui/MediaBlocks/MediaFullscreenDialog";
export { PdfBlock } from "@/components/ui/MediaBlocks/PdfBlock";
export { VideoBlock } from "@/components/ui/MediaBlocks/VideoBlock";

// Re-export context provider and hook
export {
  MediaFullscreenProvider,
  useMediaFullscreen,
} from "@/components/ui/MediaBlocks/media-fullscreen-context";

// MediaBlock router component
import { AudioBlock } from "@/components/ui/MediaBlocks/AudioBlock";
import { GenericFileBlock } from "@/components/ui/MediaBlocks/GenericFileBlock";
import { ImageBlock } from "@/components/ui/MediaBlocks/ImageBlock";
import { PdfBlock } from "@/components/ui/MediaBlocks/PdfBlock";
import { MediaBlockProps } from "@/components/ui/MediaBlocks/types";
import { VideoBlock } from "@/components/ui/MediaBlocks/VideoBlock";

/**
 * If the url is raw base64 (no data: prefix, no http), construct a proper data URL
 * so the browser can render it. The raw `format` and MIME `type` fields come from
 * the backend media object but aren't on the TS Media interface.
 */
function normalizeMediaUrl(media: MediaBlockProps["media"]): MediaBlockProps["media"] {
  const raw = media as MediaBlockProps["media"] & { format?: string };
  if (
    raw.format === "base64" &&
    raw.url &&
    !raw.url.startsWith("data:") &&
    !raw.url.startsWith("http")
  ) {
    const mime = typeof raw.type === "string" && raw.type.includes("/") ? raw.type : "image/png";
    return { ...media, url: `data:${mime};base64,${raw.url}` };
  }
  return media;
}

export const MediaBlock = ({
  media: rawMedia,
  onDelete,
  onReplace,
  readonly = true,
  size = "md",
}: MediaBlockProps) => {
  const media = normalizeMediaUrl(rawMedia);

  if (!media || !media.url) {
    return null;
  }

  let mediaType = media.type;
  if (typeof mediaType === "string") {
    if (mediaType.startsWith("image/")) {
      mediaType = "image";
    } else if (mediaType.startsWith("audio/")) {
      mediaType = "audio";
    } else if (mediaType.startsWith("video/")) {
      mediaType = "video";
    }
  }

  if (mediaType === "image") {
    return (
      <ImageBlock
        media={media}
        onDelete={onDelete}
        onReplace={onReplace}
        readonly={readonly}
        size={size}
      />
    );
  }

  if (mediaType === "application/pdf") {
    return (
      <PdfBlock
        media={media}
        onDelete={onDelete}
        onReplace={onReplace}
        readonly={readonly}
        size={size}
      />
    );
  }

  if (mediaType === "audio") {
    return (
      <AudioBlock
        media={media}
        onDelete={onDelete}
        onReplace={onReplace}
        readonly={readonly}
        size={size}
      />
    );
  }

  if (mediaType === "video") {
    return (
      <VideoBlock
        media={media}
        onDelete={onDelete}
        onReplace={onReplace}
        readonly={readonly}
        size={size}
      />
    );
  }

  // Fallback for unknown types
  return (
    <GenericFileBlock
      media={media}
      onDelete={onDelete}
      onReplace={onReplace}
      readonly={readonly}
      size={size}
    />
  );
};
